import { makeStyles } from "@mui/styles";

/**
 * Shared display constants/helpers for the Match & Energy monitoring sections
 * (Active Matches and Searching Posts).
 */

export const RANK_ORDER = [
  "IRON",
  "BRONZE",
  "UNRANKED",
  "SILVER",
  "GOLD",
  "PLATINUM",
  "DIAMOND",
  "LEGEND",
  "GRANDLEGEND",
  "IDOLIZED",
];

/** Matches whose matchRank was never set (old documents). */
export const NO_RANK_LABEL = "UNKNOWN";

/**
 * Pseudo-rank the backend reports for For fun matches (hidden MMR, no public
 * rank). Sorts after LEGEND in summaries and is a valid `rank` filter value.
 */
export const FOR_FUN_LABEL = "FOR_FUN";

export const RANK_COLORS = {
  UNRANKED: "#9e9e9e",
  IRON: "#8d6e63",
  BRONZE: "#cd7f32",
  SILVER: "#90a4ae",
  GOLD: "#c9a227",
  PLATINUM: "#00acc1",
  DIAMOND: "#5c6bc0",
  LEGEND: "#8e24aa",
  GRANDLEGEND: "#2563EB",
  IDOLIZED: "#EC4899",
  [FOR_FUN_LABEL]: "#26a69a",
  [NO_RANK_LABEL]: "#bdbdbd",
};

/** Human label for a rank key: "For fun" for the pseudo-rank, else as-is. */
export const formatRankLabel = (rank) =>
  rank === FOR_FUN_LABEL ? "For fun" : rank;

export const ROWS_PER_PAGE = 25;
export const DAY_MS = 24 * 60 * 60 * 1000;

/** "18" or "18.3" from a mean vote count. Null when the rank has no matches. */
export const formatAvgVotes = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "—";
  }
  const rounded = Math.round(Number(value) * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
};

/** Same interval the voting feed uses for the old-pagent rate. */
export const OLD_PAGENT_BOOST_INTERVAL_MS = 15 * 60 * 1000;

/** How many times the old-pagent rate has been applied for this age. */
export const oldPagentBoostSteps = (ageMs) => {
  const age = Number(ageMs);
  if (!Number.isFinite(age) || age < 0) return 0;
  return Math.floor(age / OLD_PAGENT_BOOST_INTERVAL_MS);
};

/**
 * Feed multiplier for an in-progress pagent: rate ^ steps. Null when the
 * rate is not a number greater than 0. Contests stay at 1 because the feed
 * does not apply this rate to them.
 */
export const oldPagentBoostFactor = (rate, ageMs, matchType) => {
  const value = Number(rate);
  if (!Number.isFinite(value) || value <= 0) return null;
  if (matchType === "CONTEST_POST") return 1;
  return value ** oldPagentBoostSteps(ageMs);
};

/** "1.08×", "16×", "7.9e28×". */
export const formatBoostMultiple = (value) => {
  if (!Number.isFinite(value) || value <= 0) return "—";
  if (value >= 1e6) return `${value.toExponential(1).replace("e+", "e")}×`;
  if (value >= 100) return `${Math.round(value).toLocaleString()}×`;
  if (value >= 10) {
    const rounded = Math.round(value * 10) / 10;
    return `${Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1)}×`;
  }
  return `${(Math.round(value * 100) / 100).toFixed(2)}×`;
};

/** "45m", "3h 20m", "5d 4h" from milliseconds. */
export const formatAge = (ageMs) => {
  if (ageMs === null || ageMs === undefined) return "—";
  const minutes = Math.floor(ageMs / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ${minutes % 60}m`;
  const days = Math.floor(hours / 24);
  return `${days}d ${hours % 24}h`;
};

export const ageColor = (ageMs) => {
  if (ageMs === null || ageMs === undefined) return "#333";
  if (ageMs >= 7 * DAY_MS) return "#c62828";
  if (ageMs >= DAY_MS) return "#ef6c00";
  return "#2e7d32";
};

/**
 * "#1/3" for the pagent an entry is on, or "—" when the match type has no
 * numbered run. `max` is omitted when the entry has no fixed length.
 */
export const formatPagentLabel = (pagent) => {
  if (!pagent?.number) return "—";
  return pagent.max ? `#${pagent.number}/${pagent.max}` : `#${pagent.number}`;
};

/** "KARAOKE_POST" -> "Karaoke Post" */
export const formatMatchType = (type) =>
  type
    ? type
        .replace(/_/g, " ")
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase())
    : "—";

export const rankChipSx = (rank) => ({
  backgroundColor: RANK_COLORS[rank] || RANK_COLORS[NO_RANK_LABEL],
  color: "#fff",
  fontWeight: 600,
  fontSize: "11px",
  height: "20px",
});

export const filterSelectSx = { minWidth: 160 };

export const useSectionStyles = makeStyles(() => ({
  summaryCard: {
    padding: "16px 20px",
    borderRadius: "6px",
    boxShadow:
      "rgba(0, 0, 0, 0.05) 0 6px 24px 0, rgba(0, 0, 0, 0.08) 0 0 0 1px",
  },
  listSection: {
    padding: "15px",
    borderRadius: "6px",
    boxShadow:
      "rgba(0, 0, 0, 0.05) 0 6px 24px 0, rgba(0, 0, 0, 0.08) 0 0 0 1px",
  },
  tableContainer: {
    overflowY: "auto",
    maxHeight: "600px",
    boxShadow: "none !important",
  },
  table: {
    border: "1px solid rgba(224, 224, 224, 1)",
    minWidth: "100%",
  },
  stickyHeader: {
    position: "sticky",
    top: "-1px",
    zIndex: 1,
  },
  headerCell: {
    fontSize: "13px !important",
    color: "#000 !important",
    fontWeight: "600 !important",
    padding: "8px 6px !important",
    backgroundColor: "#e0e0e0",
  },
  bodyCell: {
    fontSize: "13px !important",
    color: "#333 !important",
    padding: "6px !important",
  },
  refreshButton: {
    backgroundColor: "#142C5C !important",
    color: "#fff !important",
    borderRadius: "6px !important",
    fontSize: "14px !important",
    height: "40px !important",
    "&:hover": {
      backgroundColor: "#0f2247 !important",
    },
  },
  idText: {
    fontSize: "11px",
    fontFamily: "monospace",
    wordBreak: "break-all",
  },
  vsText: {
    margin: "0 4px",
    fontWeight: 600,
    color: "#999",
  },
}));
